import { useState, useRef, useEffect } from "react";
import { Upload, X, AlertCircle, Copy, Check, Store, Power } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";



const resolveImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  return `${BACKEND}${url}`;
};

export default function SellerProfileCard({ user, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    shop_image_url: "",
    shop_name: "",
    shop_description: "",
  });
  const [uploading, setUploading] = useState(false);
  const [shopNameChanged, setShopNameChanged] = useState(false);
  // Status buka/tutup toko. `shopOpen` adalah toggle manual seller,
  // `isOpen` adalah status efektif yang dihitung server (mempertimbangkan jam buka).
  const [shopOpen, setShopOpen] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const [togglingShop, setTogglingShop] = useState(false);
  // ID seller + slug nama toko untuk membentuk link toko yang bisa dicopas.
  // `sellerId` tetap disimpan (fallback) untuk backward compatibility dengan
  // link lama; `shopSlug` (nama-toko-friendly) jadi sumber utama link. Backend
  // `slugify_shop_name()` menormalkan nama toko jadi slug (mis. "Undead Shopp"
  // -> "undead-shopp"), dan route `/store/{seller_id_or_slug}` di server.py
  // menerima keduanya — jadi link UUID lama tetap valid.
  const [sellerId, setSellerId] = useState(user?.id || "");
  const [shopSlug, setShopSlug] = useState("");
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef(null);

  // Link publik toko seller. Dipakai untuk dibagikan/dicopas.
  // Prioritas: ``shop_slug`` (nama-toko-friendly) → fallback ke UUID seller.
  // Keduanya valid karena backend ``/store/{seller_id_or_slug}`` mendukung
  // dua bentuk sekaligus, dengan fallback slug lookup.
  const storeLink = (shopSlug || sellerId)
    ? `${(typeof window !== "undefined" && window.location?.origin) || ""}/store/${shopSlug || sellerId}`
    : "";

  // Ambil data profil toko dari backend saat komponen dimuat.
  // Backend mengembalikan ``shop_slug`` (nama-toko-friendly) di endpoint ini
  // (lihat /seller/profile GET di server.py). Slug di sini dipakai sebagai
  // path utama link toko (``/store/{shop_slug}``) — UUID seller hanya
  // fallback bila slug kosong untuk backward compatibility.
  const loadProfile = async () => {
    try {
      const { data } = await api.get("/seller/profile");
      setForm({
        shop_image_url: data.shop_image_url || "",
        shop_name: data.shop_name || "",
        shop_description: data.shop_description || "",
      });
      setShopNameChanged(!!data.shop_name_changed);
      setShopOpen(data.shop_open !== false);
      setIsOpen(!!data.is_open);
      if (data.seller_id) setSellerId(data.seller_id);
      if (data.shop_slug) setShopSlug(data.shop_slug);
    } catch (err) {
      // Diamkan jika gagal; kartu tetap tampil dengan nilai kosong.
    }
  };

  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, shop_image_url: data.url }));
      toast.success("Foto profil berhasil diupload");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload gagal");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        shop_image_url: form.shop_image_url,
        shop_name: form.shop_name.trim(),
        shop_description: form.shop_description.trim(),
      };
      const { data } = await api.put("/seller/profile", payload);
      setForm({
        shop_image_url: data.shop_image_url || "",
        shop_name: data.shop_name || "",
        shop_description: data.shop_description || "",
      });
      setShopNameChanged(!!data.shop_name_changed);
      setShopOpen(data.shop_open !== false);
      setIsOpen(!!data.is_open);
      // Update slug dari response PUT agar link toko langsung pakai slug
      // baru (mis. setelah seller mengubah nama toko). Backend mengirim
      // ``shop_slug`` yang sudah di-slugify di endpoint ini.
      if (data.shop_slug) setShopSlug(data.shop_slug);
      toast.success("Profil toko berhasil diperbarui");
      setIsEditing(false);
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal menyimpan profil");
    }
  };

  // Buka / tutup toko lewat toggle. Memanggil endpoint shop-status agar
  // status buka/tutup langsung berlaku di seluruh marketplace.
  const toggleShopOpen = async () => {
    const next = !shopOpen;
    setTogglingShop(true);
    try {
      const { data } = await api.put("/seller/shop-status", { shop_open: next });
      setShopOpen(data.shop_open !== false);
      setIsOpen(!!data.is_open);
      toast.success(next ? "Toko dibuka" : "Toko ditutup");
      if (onUpdate) onUpdate();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal mengubah status toko");
    } finally {
      setTogglingShop(false);
    }
  };

  const copyStoreLink = async () => {
    if (!storeLink) return;
    try {
      await navigator.clipboard.writeText(storeLink);
      setCopied(true);
      toast.success("Link toko disalin");
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error("Gagal menyalin link");
    }
  };

  return (
    <div className="border border-white/10 p-6 mb-8" data-testid="seller-info-card">
      <div className="flex items-center justify-between mb-6">
        <div>
          <span className="tag tag-accent">// INFO SELLER</span>
          <h2 className="font-display font-black text-2xl text-white mt-2">Info Seller</h2>
        </div>
        {!isEditing && (
          <button
            onClick={() => setIsEditing(true)}
            className="px-4 py-2 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 text-xs uppercase font-bold tracking-widest"
          >
            Edit Profil
          </button>
        )}
      </div>

      {!isEditing ? (
        // View Mode
        <div className="space-y-6">
          {/* Foto Profil */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 block mb-3">Foto Profil</label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 border border-white/15 rounded-lg overflow-hidden bg-black/50 flex items-center justify-center">
                {form.shop_image_url ? (
                  <img src={resolveImg(form.shop_image_url)} alt="Profil" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-white/30 text-center text-xs">Belum ada foto</div>
                )}
              </div>
              <div className="text-sm text-white/60">
                {form.shop_image_url ? (
                  <p className="text-[#00FF66]">✓ Foto profil sudah diatur</p>
                ) : (
                  <p>Belum ada foto profil. Tambahkan foto (upload atau via link) untuk meningkatkan kepercayaan pembeli.</p>
                )}
              </div>
            </div>
          </div>

          {/* Nama Toko */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 block mb-2">Nama Toko</label>
            <p className="text-lg font-bold text-white">{form.shop_name || "Belum diatur"}</p>
            <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
              {shopNameChanged ? "✗ Sudah diubah 1x (tidak bisa diubah lagi)" : "Bisa diubah 1x"}
            </p>
          </div>

          {/* Status Toko (Buka/Tutup) */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 block mb-2">Status Toko</label>
            <div className="flex items-center gap-4 flex-wrap">
              <button
                type="button"
                onClick={toggleShopOpen}
                disabled={togglingShop}
                data-testid="seller-shop-toggle"
                className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors disabled:opacity-50 ${
                  shopOpen ? "bg-[#00FF66]" : "bg-white/20"
                }`}
                aria-pressed={shopOpen}
                title={shopOpen ? "Klik untuk tutup toko" : "Klik untuk buka toko"}
              >
                <span
                  className={`inline-block h-6 w-6 rounded-full bg-black shadow transform transition-transform ${
                    shopOpen ? "translate-x-9" : "translate-x-1"
                  }`}
                />
              </button>
              <div className="flex items-center gap-2">
                <Power className={`w-4 h-4 ${shopOpen ? "text-[#00FF66]" : "text-white/40"}`} />
                <span className={`text-sm font-bold ${shopOpen ? "text-[#00FF66]" : "text-white/50"}`}>
                  {shopOpen ? "TOKO BUKA" : "TOKO TUTUP"}
                </span>
              </div>
              {shopOpen && !isOpen && (
                <span className="text-[10px] uppercase tracking-widest text-[#FFD700] border border-[#FFD700]/40 px-2 py-1">
                  Di luar jam buka
                </span>
              )}
            </div>
            <p className="text-[10px] uppercase tracking-widest text-white/40 mt-2">
              {shopOpen
                ? "Toko aktif. Pembeli bisa melihat & membeli produk."
                : "Toko ditutup. Produk tidak bisa dibeli sampai dibuka kembali."}
            </p>
          </div>

          {/* Link Toko (bisa dicopas) */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 block mb-2">Link Toko</label>
            <div className="flex items-center gap-2">
              <div className="flex-1 flex items-center gap-2 bg-black border border-white/15 p-3 min-w-0">
                <Store className="w-4 h-4 text-[#00F0FF] shrink-0" />
                <span className="text-sm text-white/80 truncate" data-testid="seller-store-link">
                  {storeLink || "Menyiapkan link..."}
                </span>
              </div>
              <button
                type="button"
                onClick={copyStoreLink}
                disabled={!storeLink}
                data-testid="seller-store-link-copy"
                className="inline-flex items-center gap-2 px-4 py-3 border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 text-xs uppercase font-bold tracking-widest disabled:opacity-50"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                {copied ? "Tersalin" : "Copy"}
              </button>
            </div>
            <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
              Bagikan link ini agar pembeli langsung menuju halaman tokomu.
            </p>
          </div>

          {/* Deskripsi Toko */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 block mb-2">Deskripsi Toko</label>
            <p className="text-sm text-white/80 whitespace-pre-wrap">
              {form.shop_description || "Belum ada deskripsi"}
            </p>
          </div>
        </div>
      ) : (
        // Edit Mode
        <form onSubmit={handleSave} className="space-y-4">
          {/* Foto Profil Upload */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 block mb-3">Foto Profil</label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 border border-white/15 rounded-lg overflow-hidden bg-black/50 flex items-center justify-center relative">
                {form.shop_image_url ? (
                  <>
                    <img src={resolveImg(form.shop_image_url)} alt="Profil" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, shop_image_url: "" })}
                      className="absolute top-0 right-0 bg-black/70 text-white p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <div className="text-white/30 text-center text-xs">Belum ada foto</div>
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  data-testid="seller-profile-photo-input"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="btn-ghost inline-flex items-center gap-2 disabled:opacity-50 mb-2"
                >
                  <Upload className="w-4 h-4" />
                  {uploading ? "Uploading..." : "Upload Foto"}
                </button>
                <p className="text-[10px] text-white/40">atau paste URL di bawah</p>
              </div>
            </div>
            <input
              type="text"
              value={form.shop_image_url}
              onChange={(e) => setForm({ ...form, shop_image_url: e.target.value })}
              placeholder="https://... atau /api/files/..."
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm mt-2"
              data-testid="seller-profile-photo-url"
            />
          </div>

          {/* Nama Toko */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 block mb-2">
              Nama Toko
              {shopNameChanged && (
                <span className="text-[#FF003C] ml-2 inline-flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Tidak bisa diubah lagi
                </span>
              )}
            </label>
            <input
              type="text"
              value={form.shop_name}
              onChange={(e) => setForm({ ...form, shop_name: e.target.value })}
              disabled={shopNameChanged}
              placeholder="Nama toko Anda"
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="seller-shop-name-input"
            />
            <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
              {shopNameChanged
                ? "Nama toko sudah diubah 1x. Untuk mengubah lagi, hubungi admin."
                : "Anda bisa mengubah nama toko 1x. Setelah itu, hubungi admin untuk perubahan berikutnya."}
            </p>
          </div>

          {/* Deskripsi Toko */}
          <div>
            <label className="text-xs uppercase tracking-[0.2em] text-white/60 block mb-2">Deskripsi Toko</label>
            <textarea
              value={form.shop_description}
              onChange={(e) => setForm({ ...form, shop_description: e.target.value })}
              placeholder="Ceritakan tentang toko Anda, produk unggulan, dll..."
              rows={4}
              maxLength={500}
              className="w-full bg-black border border-white/15 focus:border-[#00F0FF] p-3 text-sm"
              data-testid="seller-shop-description-input"
            />
            <p className="text-[10px] uppercase tracking-widest text-white/40 mt-1">
              {form.shop_description.length}/500 karakter
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-4 border-t border-white/10">
            <button
              type="submit"
              className="flex-1 px-4 py-3 bg-[#00F0FF] text-black hover:bg-[#33F0FF] font-display font-black uppercase tracking-widest text-xs"
              data-testid="seller-profile-save-btn"
            >
              Simpan Profil
            </button>
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                loadProfile();
              }}
              className="px-4 py-3 border border-white/15 text-white/70 text-xs uppercase"
            >
              Batal
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
